import * as React from 'react';
import { GitBranch, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ImageSlot } from '@/components/ui/image-slot';
import { PostureChip, type PostureStatus } from '@/components/ui/posture-chip';
import { cn } from '@/lib/utils/cn';

/**
 * Engagement-overview hero (T-0097 / E10-F03 hero pattern).
 *
 * Visual contract — see README §7 ASCII diagram + the prototype's
 * `screens-engagement.js → window.Screens.overview` and the
 * `.eng-hero*` rules in `gaps.css`:
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │  <ImageSlot> hero background (gradient overlay baked in)    │
 *   │  ┌─ city pill ──┐               ┌─ <PostureChip dot> ─┐     │
 *   │  └──────────────┘               └────────────────────┘      │
 *   │                                                             │
 *   │  Project name — Source Serif 4, 36px, white                 │
 *   │  UAT · vendor Cascadia Public Systems                       │
 *   │  [Capture snapshot]  [Generate PDF]                         │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Why this is its own component:
 *   - The hero is the first thing every user sees on every engagement.
 *     The prototype calls it the "newest visual pattern" (README §7).
 *     Pixel fidelity matters here more than anywhere else in E10
 *     (per the ticket §7 implementer's notes).
 *   - The `<ImageSlot>` drag-drop primitive (T-0095) supplies the
 *     background image + the gradient overlay; this component fills
 *     the slot with the title strip.
 *   - The CTA buttons need to be physically separable from the page
 *     layout so the parent route can wire them to the snapshot-capture
 *     and pdf-render mutations without colocating side-effects with
 *     the visual.
 *
 * Layout details mirroring the prototype:
 *   - 280px tall hero (matches `.eng-hero { height: 280px }`).
 *   - 22px / 28px padding (`padding: 22px 28px` in `.eng-hero-body`).
 *   - Top row: city pill at left, posture pill at right, both
 *     anchored to the top edge.
 *   - Bottom block: title (auto-margined to push to bottom),
 *     subtitle, then CTAs in a row with 8px gap.
 *
 * City pill style: ink-900/36% bg + blurred backdrop + white-12% border,
 * matches `.eng-hero-meta` in gaps.css. The crumb separator uses an
 * inert middle-dot character.
 *
 * Posture pill style: the prototype renders a custom white-bg pill with
 * a shadow. The TA contract's load-bearing primitive is `<PostureChip>`
 * — README §6 calls posture chips "load-bearing UI primitives ... build
 * once in apps/web/src/components/ui and reuse everywhere." We honor
 * that here and pass `dot` to get the colored dot prefix. Wrapping it
 * in a `.bg-paper-0/95` ring keeps the chip readable against any
 * background photo (and matches the prototype's white-pill silhouette
 * over the navy gradient).
 */
export interface EngagementHeroProps {
  /** Engagement id (drives both the ImageSlot persistence + analytics). */
  engagementId: string;
  /** Project id — required so ImageSlot can initiate a chunked upload. */
  projectId?: string;
  /** Client display name — drives the city pill's primary crumb. */
  clientName: string;
  /** Optional firm name — appended to the city pill ("IV&V by {firm}"). */
  firmName?: string;
  /**
   * The service the firm provides on this engagement, before "by {firm}" in
   * the city pill. The prototype says "IV&V"; a Discovery and Selection
   * engagement is not an IV&V one (`serviceLabel` in `lib/engagement-modules`).
   */
  serviceLabel?: string;
  /** Engagement title — rendered in Source Serif 4 36px white. */
  engagementTitle: string;
  /** Subtitle one-liner (phase + vendor). */
  subtitle?: React.ReactNode;
  /**
   * Posture status driving the right-anchored chip. Translate from the
   * verification-rollup palette via `rollupColorToPostureStatus()` in
   * `lib/api/health-posture.ts` before passing. Omit it for an engagement
   * with no IV&V module: posture is an IV&V roll-up, so the pill stays away
   * rather than say "Unassessed" about something that is never assessed.
   */
  posture?: PostureStatus;
  /** Initial banner image URL (from EngagementDto.banner_image_url). */
  bannerImageUrl?: string | null;
  /** Fires after the ImageSlot's chunked upload resolves a banner URL. */
  onBannerUploadComplete?: (url: string) => void;
  /** Wires the "Capture snapshot" CTA. Disabled when omitted. */
  onCaptureSnapshot?: () => void;
  /** Wires the "Generate PDF" CTA. Disabled when omitted. */
  onGeneratePdf?: () => void;
  /** Whether the snapshot capture is in flight. */
  capturingSnapshot?: boolean;
  /** Whether the PDF render is in flight. */
  generatingPdf?: boolean;
}

/** Prototype slug helper — matches `screens-engagement.js`:
 *   slugClient = (e.client || '').toLowerCase().replace(/[^a-z]+/g, '-')
 *
 * The crumb pattern in the prototype is `${client} · ${slug} · IV&V by Firm`,
 * which doubles as a quick visual identifier for which environment / client
 * tenant the overview is rendered against.
 */
function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function EngagementHero({
  engagementId,
  projectId,
  clientName,
  firmName,
  serviceLabel = 'IV&V',
  engagementTitle,
  subtitle,
  posture,
  bannerImageUrl,
  onBannerUploadComplete,
  onCaptureSnapshot,
  onGeneratePdf,
  capturingSnapshot,
  generatingPdf,
}: EngagementHeroProps) {
  const placeholder = `Drop a city / project photo for ${clientName} · 1600×400 recommended`;
  const clientSlug = slugify(clientName);

  return (
    <ImageSlot
      engagementId={engagementId}
      projectId={projectId}
      defaultImageUrl={bannerImageUrl ?? null}
      onUploadComplete={onBannerUploadComplete}
      placeholder={placeholder}
      // Hero geometry — matches `.eng-hero { height: 280px; ... }`.
      className="h-[280px] rounded-large"
    >
      <div className="flex h-full w-full flex-col justify-between px-7 py-[22px] text-paper-0">
        {/* Top row: city pill (left) + posture pill (right) */}
        <div className="flex items-start justify-between gap-4">
          <div
            className={cn(
              'inline-flex items-center gap-[6px] rounded-full border px-3 py-[5px]',
              'text-meta font-medium',
              'border-paper-0/15 bg-ink-900/30 text-paper-0/80',
              // Backdrop-blur falls back gracefully when unsupported; the
              // semi-transparent navy bg is the primary readability lever.
              'backdrop-blur-md',
            )}
          >
            <span className="font-medium">{clientName}</span>
            {clientSlug ? (
              <>
                <span aria-hidden="true" className="text-paper-0/40">
                  ·
                </span>
                <span className="font-mono text-[11px] text-paper-0/70">{clientSlug}</span>
              </>
            ) : null}
            {firmName ? (
              <>
                <span aria-hidden="true" className="text-paper-0/40">
                  ·
                </span>
                <span className="font-medium">
                  {serviceLabel} by {firmName}
                </span>
              </>
            ) : null}
          </div>
          {posture ? (
            <div className="inline-flex items-center rounded-full bg-paper-0/95 px-1 shadow-modal">
              <PostureChip status={posture} dot />
            </div>
          ) : null}
        </div>

        {/* Bottom block: title, subtitle, CTAs */}
        <div>
          <h1
            className={cn(
              // Source Serif 4 · 36px · 600 · white · drop-shadow for legibility
              'font-serif text-[36px] font-semibold leading-[1.1] tracking-[-0.015em]',
              'max-w-[720px] text-paper-0',
              '[text-shadow:0_2px_12px_rgb(24_46_88_/_0.55)]',
            )}
          >
            {engagementTitle}
          </h1>
          {subtitle ? (
            <p
              className={cn(
                'mt-1.5 text-body text-paper-0/85',
                '[text-shadow:0_1px_8px_rgb(24_46_88_/_0.6)]',
              )}
            >
              {subtitle}
            </p>
          ) : null}
          <div className="mt-3.5 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onCaptureSnapshot}
              disabled={!onCaptureSnapshot || capturingSnapshot}
              // Glass-on-photo styling — opaque white background +
              // navy text mirrors `.eng-hero-cta .btn-secondary` in
              // gaps.css.
              className="bg-paper-0/95 text-ink-900 hover:bg-paper-0"
            >
              <GitBranch className="size-3.5" aria-hidden="true" />
              {capturingSnapshot ? 'Capturing…' : 'Capture snapshot'}
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={onGeneratePdf}
              disabled={!onGeneratePdf || generatingPdf}
              // Primary CTA — Avero orange. Shadow matches the
              // `.eng-hero-cta .btn-primary { box-shadow: 0 2px 10px
              // rgba(201,117,43,0.45) }` rule from gaps.css.
              className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-500 shadow-[0_2px_10px_rgb(201_117_43_/_0.45)]"
            >
              <Download className="size-3.5" aria-hidden="true" />
              {generatingPdf ? 'Generating…' : 'Generate PDF'}
            </Button>
          </div>
        </div>
      </div>
    </ImageSlot>
  );
}
